import type { TimelineElement } from '../../types/timelineElement'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { MobileNlsT } from '../Mobile/MobileNls'
import { getHelperFieldSubtabNlsLabel } from '../Mobile/MobileNls'
import { isModuleEnabled } from '../../utils/workspaceModules'

export type HelperFieldSubtab = 'TODAY' | 'SCAN' | 'COUNT' | 'ME'

export type WorkspaceHelperModules = {
  stockEnabled?: boolean
  formsEnabled?: boolean
  helperScanEnabled?: boolean
  helperCountEnabled?: boolean
  countEnabled?: boolean
  helperFormsEnabled?: boolean
}

export type HelperFieldPolicy = {
  hidden?: HelperFieldSubtab[]
}

export const HELPER_FIELD_SUBTABS: ReadonlyArray<{
  key: HelperFieldSubtab
  label: string
}> = [
  { key: 'TODAY', label: 'Today' },
  { key: 'SCAN', label: 'Scan' },
  { key: 'COUNT', label: 'Count' },
  { key: 'ME', label: 'Me' },
]

const FIELD_SUBTAB_KEYS = new Set<HelperFieldSubtab>(
  HELPER_FIELD_SUBTABS.map((entry) => entry.key),
)

export const normalizeHelperFieldSubtab = (
  value: unknown,
  fallback: HelperFieldSubtab = 'TODAY',
): HelperFieldSubtab => {
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase()
    if (FIELD_SUBTAB_KEYS.has(normalized as HelperFieldSubtab)) {
      return normalized as HelperFieldSubtab
    }
  }
  return FIELD_SUBTAB_KEYS.has(fallback) ? fallback : 'TODAY'
}

export const computeVisibleFieldSubtabs = (
  modules: WorkspaceHelperModules | undefined,
  policy?: HelperFieldPolicy,
): HelperFieldSubtab[] => {
  const m = modules ?? {}
  const stockAvailable = isModuleEnabled({ modules: m }, 'stock')
  const hiddenPolicy = new Set(policy?.hidden ?? [])

  return (['TODAY', 'SCAN', 'COUNT', 'ME'] as const).filter((subtab) => {
    if (subtab === 'TODAY') return true
    if (hiddenPolicy.has(subtab)) return false
    if (subtab === 'SCAN') return m.helperScanEnabled !== false
    if (subtab === 'COUNT') {
      const countAllowed = m.helperCountEnabled !== false && m.countEnabled !== false
      return countAllowed && stockAvailable
    }
    if (subtab === 'ME') return true
    return true
  })
}

export const getHelperFieldSubtabLabel = (subtab: HelperFieldSubtab, t?: MobileNlsT): string =>
  t
    ? getHelperFieldSubtabNlsLabel(subtab, t)
    : HELPER_FIELD_SUBTABS.find((entry) => entry.key === subtab)?.label ?? 'Today'

// Backward-compatibility aliases for existing imports during R26/R27
export type HelperControlsSubtab = HelperFieldSubtab
export const HELPER_CONTROLS_SUBTABS = HELPER_FIELD_SUBTABS
export const normalizeHelperControlsSubtab = normalizeHelperFieldSubtab
export const computeVisibleHelperControlsSubtabs = computeVisibleFieldSubtabs
export const getHelperControlsSubtabLabel = getHelperFieldSubtabLabel

export type HelperScanTarget =
  | { type: 'WORKSPACE'; id: string; title: string; subtitle: string }
  | { type: 'CHECKLIST'; id: string; title: string; subtitle: string; taskId: string | null }
  | { type: 'GOAL' | 'TASK' | 'SUBTASK'; id: string; title: string; subtitle: string }

const targetRank = (target: HelperScanTarget): number => {
  if (target.type === 'WORKSPACE') return 0
  if (target.type === 'CHECKLIST') return 1
  if (target.type === 'TASK') return 2
  if (target.type === 'SUBTASK') return 3
  return 4
}

export const buildHelperScanTargets = (input: {
  workspaceId: string | null
  workspaceName?: string | null
  items: readonly HelperAssignedItem[]
  elements: readonly Pick<TimelineElement, 'id' | 'workspaceId' | 'type' | 'title'>[]
  t?: MobileNlsT
}): HelperScanTarget[] => {
  if (!input.workspaceId) return []
  const t = input.t
  const workspaceTarget: HelperScanTarget = {
    type: 'WORKSPACE',
    id: input.workspaceId,
    title: input.workspaceName || (t ? t('NLSHelperScanWorkspaceTitleFallback') : 'Workspace'),
    subtitle: t ? t('NLSHelperScanWorkspaceSubtitle') : 'Overall workspace inventory',
  }
  const checklistTargets = input.items.map((item): HelperScanTarget => ({
    type: 'CHECKLIST',
    id: item.id,
    title: item.title,
    subtitle: t
      ? t('NLSHelperScanChecklistSubtitle', { status: item.effectiveStatus.toLowerCase() })
      : `Checklist · ${item.effectiveStatus.toLowerCase()}`,
    taskId: typeof item.parentContainerId === 'string' && item.parentContainerId.trim().length > 0
      ? item.parentContainerId
      : null,
  }))
  const elementTargets = input.elements
    .filter((element) => element.workspaceId === input.workspaceId)
    .map((element): HelperScanTarget => ({
      type: element.type,
      id: element.id,
      title: element.title,
      subtitle: element.type.toLowerCase(),
    }))
  return [workspaceTarget, ...checklistTargets, ...elementTargets]
    .sort((left, right) => targetRank(left) - targetRank(right) || left.title.localeCompare(right.title))
}

export const filterHelperScanTargets = (
  targets: readonly HelperScanTarget[],
  query: string,
): HelperScanTarget[] => {
  const needle = query.trim().toLowerCase()
  if (!needle) return [...targets]
  return targets.filter((target) =>
    target.title.toLowerCase().includes(needle)
    || target.subtitle.toLowerCase().includes(needle)
    || target.type.toLowerCase().includes(needle),
  )
}
